import { type FC, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CALENDAR_INTERVAL_OPTIONS, DEFAULT_SELECTED_KEYS, FREQUENCY_MAP } from '../../config/calendarConstants';
import type { CalendarIntervalType, CalendarRow } from '../../types/calendar';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { sendNotification } from 'utils/helperMethods';
import useCalendarData from './hooks/queries/useCalendarData';
import useOptimizationId from './hooks/queries/useOptimizationId';
import useOptimizationPolling from './hooks/queries/useOptimizationPolling';
import { useCalendarSort } from './hooks/ui/useCalendarSort';
import { useContainerWidth } from './hooks/ui/useContainerWidth';
import { useCalendarColumns } from './hooks/ui/useCalendarColumns';
import { usePromoTypeLegend } from './hooks/ui/usePromoTypeLegend';
import { useCalendarRowActions } from './hooks/ui/useCalendarRowActions';
import CalendarToolbar from './CalendarToolbar';
import CalendarTable from './CalendarTable';
import PlanDetailsFooter from '../components/PlanDetailsFooter';
import ViewSelectModal from '../components/ViewSelectModal';
import DeletePlanModal from './components/DeletePlanModal';
import DeleteAllPromotionsModal from './components/DeleteAllPromotionsModal';
import DeleteEventModal from './components/DeleteEventModal';
import ImportEventFromPlanModal from './components/ImportEventFromPlanModal';
import { OptimizePlanModal } from './components/OptimizePlan';
import { OptimiseResultModal } from './components/OptimiseResult';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import { LABELS as tpoGenLabels } from '../../config/labels';
import type { RunOptimizationResponse } from './hooks/mutations/useRunOptimizationMutation';

interface CalendarViewProps {
    planId: string;
    planName: string;
}

export const CalendarView: FC<CalendarViewProps> = ({ planId, planName }) => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { currentPlanDetails, filterState } = usePlanDetailsContext();
    const tpoGenConfig = useTpoGenConfig();

    const [calendarInterval, setCalendarInterval] = useState<CalendarIntervalType>('weekly');
    const [showIntervalModal, setShowIntervalModal] = useState(false);
    const [selectedColumnKeys, setSelectedColumnKeys] = useState<string[]>(DEFAULT_SELECTED_KEYS);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [groupBy, setGroupBy] = useState('none');
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const [showChanges, setShowChanges] = useState(false);
    const [showDeletePlan, setShowDeletePlan] = useState(false);
    const [showDeleteAllPromotions, setShowDeleteAllPromotions] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [pendingDeleteEvent, setPendingDeleteEvent] = useState<{ eventId: string; eventName: string } | null>(null);
    const [showOptimizePlanModal, setShowOptimizePlanModal] = useState(false);
    const [showOptimiseResultModal, setShowOptimiseResultModal] = useState(false);
    const [optimizationJobId, setOptimizationJobId] = useState<string | null>(null);
    const [optimizedPlanId, setOptimizedPlanId] = useState<string | null>(null);
    const [showOptimizedCalendar, setShowOptimizedCalendar] = useState(false);
    const [isInfeasible, setIsInfeasible] = useState(false);
    const [infeasibleMessage, setInfeasibleMessage] = useState<string | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const isUserTriggeredOptimization = useRef(false);
    const erroredJobIdRef = useRef<string | null>(null);

    const { optimizationId: existingOptimizationId } = useOptimizationId(planId);

    useEffect(() => {
        if (existingOptimizationId && !optimizationJobId && erroredJobIdRef.current !== existingOptimizationId) {
            setOptimizationJobId(existingOptimizationId);
        }
    }, [existingOptimizationId, optimizationJobId]);

    const handleOptimizationCompleted = useCallback((completedPlanId: string | null) => {
        if (isUserTriggeredOptimization.current) {
            sendNotification('success', 'Optimization completed successfully.');
        }
        if (completedPlanId) {
            setOptimizedPlanId(completedPlanId);
            setShowOptimizedCalendar(true);
        }
    }, []);

    const handleOptimizationError = useCallback(() => {
        if (isUserTriggeredOptimization.current) {
            sendNotification('error', tpoGenLabels.optimizePlan.notifications.optimizationFailed);
        }
        setOptimizationJobId(prev => {
            erroredJobIdRef.current = prev;
            return null;
        });
    }, []);

    const handleOptimizationInfeasible = useCallback((message: string | null) => {
        if (isUserTriggeredOptimization.current) {
            sendNotification('info', tpoGenLabels.optimizePlan.notifications.infeasibleToast);
        }
        setIsInfeasible(true);
        setInfeasibleMessage(message);
        setOptimizationJobId((prev) => {
            erroredJobIdRef.current = prev;
            return null;
        });
    }, []);

    const { optimizationStatus, optimizedPlanId: polledOptimizedPlanId } = useOptimizationPolling({
        jobId: optimizationJobId,
        onCompleted: handleOptimizationCompleted,
        onError: handleOptimizationError,
        onInfeasible: handleOptimizationInfeasible,
    });

    useEffect(() => {
        if (optimizationStatus === 'completed' && polledOptimizedPlanId && !optimizedPlanId) {
            setOptimizedPlanId(polledOptimizedPlanId);
            setShowOptimizedCalendar(true);
        }
    }, [optimizationStatus, polledOptimizedPlanId, optimizedPlanId]);

    const { sortBy, sortOrder, handleTableChange, getColumnSortOrder } = useCalendarSort();
    const containerWidth = useContainerWidth(containerRef);

    const frequency = FREQUENCY_MAP[calendarInterval];
    const activePlanId = showOptimizedCalendar && optimizedPlanId ? optimizedPlanId : planId;
    const calendarData = useCalendarData(
        activePlanId,
        frequency,
        searchKeyword,
        groupBy,
        selectedColumnKeys,
        filterState.filters,
        sortBy,
        sortOrder,
    );

    const handleOptimizationSubmitFinish = useCallback((data: RunOptimizationResponse) => {
        isUserTriggeredOptimization.current = true;
        setOptimizationJobId(data.job_id);
        setIsInfeasible(false);
        setInfeasibleMessage(null);
        setShowOptimizePlanModal(false);
    }, []);

    const handleRequestDeleteEvent = useCallback((record: CalendarRow) => {
        setPendingDeleteEvent({ eventId: record.event_id, eventName: record.event_name });
    }, []);

    const { getRowActionMenuItems, handleEditEvent, handleEditPromo } = useCalendarRowActions({
        onRequestDeleteEvent: handleRequestDeleteEvent,
    });
    const legend = usePromoTypeLegend(calendarData.dataSource, calendarData.periods);
    const handleToggleShowChanges = useCallback(() => {
        setShowChanges((prev) => !prev);
    }, []);

    const handleClickInterval = useCallback(() => setShowIntervalModal(true), []);
    const handleApplyInterval = useCallback((value: CalendarIntervalType) => {
        setCalendarInterval(value);
        setShowIntervalModal(false);
    }, []);

    const columns = useCalendarColumns({
        periods: calendarData.periods,
        frequency,
        containerWidth,
        expandedRowKeys,
        setExpandedRowKeys,
        onClickInterval: handleClickInterval,
        selectedColumnKeys,
        sortBy,
        sortOrder,
        getColumnSortOrder,
        getRowActionMenuItems,
        showChanges,
        onEditEvent: handleEditEvent,
        onEditPromo: handleEditPromo,
    });

    const isCalendarDataLoading =
        calendarData.isPending || (calendarData.isFetching && !calendarData.isFetchingNextPage);

    return (
        <>
            <CalendarToolbar
                planId={planId}
                selectedColumnKeys={selectedColumnKeys}
                setSelectedColumnKeys={setSelectedColumnKeys}
                searchKeyword={searchKeyword}
                setSearchKeyword={setSearchKeyword}
                groupBy={groupBy}
                setGroupBy={setGroupBy}
                filterState={filterState}
                showChanges={showChanges}
                onToggleShowChanges={handleToggleShowChanges}
                onShowDeletePlan={() => setShowDeletePlan(true)}
                onShowDeleteAllPromotions={() => setShowDeleteAllPromotions(true)}
                onShowImportModal={() => setShowImport(true)}
                onShowOptimizePlan={() => setShowOptimizePlanModal(true)}
                onShowOptimisationResults={() => setShowOptimiseResultModal(true)}
                isOptimizationInProgress={optimizationStatus === 'in_progress'}
                optimizationCompleted={!!optimizedPlanId}
                showOptimizedCalendar={showOptimizedCalendar}
                onToggleOptimizedView={() => setShowOptimizedCalendar((prev) => !prev)}
                isInfeasible={isInfeasible}
                infeasibleMessage={infeasibleMessage}
                isDataLoading={isCalendarDataLoading}
            />

            <CalendarTable
                ref={containerRef}
                dataSource={calendarData.dataSource}
                columns={columns}
                isPending={calendarData.isPending}
                isFetching={calendarData.isFetching}
                isFetchingNextPage={calendarData.isFetchingNextPage}
                hasNextPage={calendarData.hasNextPage}
                fetchNextPage={calendarData.fetchNextPage}
                expandedRowKeys={expandedRowKeys}
                setExpandedRowKeys={setExpandedRowKeys}
                handleTableChange={handleTableChange}
                legend={legend}
                showChanges={showChanges}
            />

            <PlanDetailsFooter
                loadedCount={calendarData.loadedCount}
                totalCount={calendarData.totalCount}
                createdBy={currentPlanDetails?.created_by}
            />

            <DeletePlanModal
                open={showDeletePlan}
                onClose={() => setShowDeletePlan(false)}
                planId={planId}
                planName={currentPlanDetails?.plan_name || planName}
                onSuccess={() => navigate(`/tpo-gen/plans-index${state?.fromSearch ?? ''}`)}
            />
            <DeleteAllPromotionsModal
                open={showDeleteAllPromotions}
                onClose={() => setShowDeleteAllPromotions(false)}
                planId={planId}
                planName={currentPlanDetails?.plan_name || planName || planId}
            />
            <DeleteEventModal
                open={!!pendingDeleteEvent}
                onClose={() => setPendingDeleteEvent(null)}
                eventId={pendingDeleteEvent?.eventId ?? ''}
                eventName={pendingDeleteEvent?.eventName ?? ''}
            />
            <ImportEventFromPlanModal open={showImport} onClose={() => setShowImport(false)} targetPlanId={planId} />

            <OptimizePlanModal
                open={showOptimizePlanModal}
                onCancel={() => setShowOptimizePlanModal(false)}
                onSubmitFinish={handleOptimizationSubmitFinish}
                planId={planId}
                config={tpoGenConfig.optimizePlanConfig}
            />

            <OptimiseResultModal
                open={showOptimiseResultModal}
                onClose={() => setShowOptimiseResultModal(false)}
                jobId={optimizationJobId}
                planId={planId}
            />
            <ViewSelectModal<CalendarIntervalType>
                open={showIntervalModal}
                title="Calendar View"
                label="Choose Calendar view"
                options={CALENDAR_INTERVAL_OPTIONS}
                initialValue={calendarInterval}
                onCancel={() => setShowIntervalModal(false)}
                onApply={handleApplyInterval}
            />
        </>
    );
};

export default CalendarView;
